// How a goal reads past its target and against its time window (#156). Pure rules,
// shared by the goal card and the detail view (and unit-tested in test/goalOver.test.js),
// so "+42% over target", "2.4× target" and "27 days left" read the same everywhere.
//
// Only "at least" goals (higher is better) count past 100%. "Under a cap", range and
// mix goals keep their own meaning for a % above 100 and are left alone.

// Is this goal past its target in the "more is better" sense?
export function isOver(goal = {}, p = {}) {
  const dir = goal.direction || p.direction || 'at_least';
  return dir === 'at_least' && Number.isFinite(p.pct) && p.pct >= 100;
}

// 2.4× — one decimal, rounded DOWN so "2.0×" is only shown once it's truly doubled.
export function multLabel(pct) {
  return (Math.floor(Number(pct) / 10) / 10).toFixed(1).replace(/\.0$/, '') + '×';
}

// The % shown in a ring/dial centre or the "of" line: exact up to 999%, then the
// multiple, so it never overflows the shape.
export function pctLabel(pct) {
  const n = Math.round(Number(pct));
  if (!Number.isFinite(n)) return '—';
  return n < 1000 ? `${n}%` : multLabel(n);
}

// The one-line over/under read under the number. tone: 'good' | 'warn' | 'muted'.
//   ≥ 2×   → "2.4× target" + "+140%"
//   ≥ 100% → "+42% over target"
//   < 100% → "26% short" (behind) or "26% to go", + what's left and the daily rate
//            needed for the days left in the window.
export function overLine(goal = {}, p = {}, fmt = (v) => String(v)) {
  const dir = goal.direction || p.direction || 'at_least';
  if (dir !== 'at_least' || !Number.isFinite(p.pct) || p.value == null || !goal.targetValue) return null;
  const pct = Math.round(p.pct);
  if (pct >= 200) return { tone: 'good', text: `${multLabel(pct)} target`, sub: `+${(pct - 100).toLocaleString('en-ZA')}%` };
  if (pct >= 100) return { tone: 'good', text: `+${pct - 100}% over target`, sub: '' };
  const left = Number(goal.targetValue) - Number(p.value);
  const w = p.window;
  const days = w && !w.closed && w.daysLeft > 0 ? w.daysLeft : null;
  const parts = [`${fmt(Math.round(left))} to go`];
  if (days) parts.push(`~${fmt(Math.ceil(left / days))}/day`);
  return { tone: p.status === 'behind' ? 'warn' : 'muted', text: `${100 - pct}% ${p.status === 'behind' ? 'short' : 'to go'}`, sub: p.status === 'behind' ? parts.join(' · ') : (days ? parts[1] : '') };
}

// "✓ 2× target" — whole multiples only, matching the achievement record's marks.
export function multipleChip(goal = {}, p = {}) {
  if (!isOver(goal, p) || p.pct < 200) return '';
  return `✓ ${Math.floor(p.pct / 100)}× target`;
}

// Short date: "3 Oct" (adds the year only when it isn't this year).
export function shortDate(iso, today = new Date()) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const opts = { day: 'numeric', month: 'short', ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}) };
  return d.toLocaleDateString('en-ZA', opts);
}

// The card's quiet time line (option C): "27 days left · Hit 22 Oct" /
// "Closed 24 Oct · Hit 3 Oct". Returns null when there's nothing to say.
export function timeLine(p = {}, today = new Date()) {
  const w = p.window;
  const hitAt = p.achievement && p.achievement.hitAt;
  let left = '';
  if (w) left = w.closed ? `Closed ${shortDate(w.end, today)}` : w.daysLeft === 0 ? 'Last day' : `${w.daysLeft} day${w.daysLeft === 1 ? '' : 's'} left`;
  const hit = hitAt ? `Hit ${shortDate(hitAt, today)}` : '';
  if (!left && !hit) return null;
  return { left, hit };
}

// Plain-language names for the record's whole multiples.
export function multipleName(k) {
  return k === 1 ? 'Target hit' : k === 2 ? 'Doubled (2×)' : k === 3 ? 'Tripled (3×)' : `${k}× target`;
}
